import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';

import ConfirmModal from '../../components/confirm-modal/ConfirmModal';
import CheckBox from '../../../components/form/checkbox/CheckBox';
import Form from '../../../components/form/form/Form';
import FormLabel from '../../../components/form/form-label/FormLabel';
import { RootState } from '../../../core/state/state';
import { getNotificationTypes, saveNotificationType } from '../../../core/state/notifications/asyncActions';
import { RequestNotificationType } from '../../../core/state/notifications/types';
import { useInternalUser } from '../../../core/hooks/useInternalUser';

import './ManageNotifications.scss';

interface Props {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const ManageNotifications: React.FC<Props> = ({ isOpen, setIsOpen }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const notificationTypes = useSelector((state: RootState) => state.notifications.notificationTypes);
  const isInternalUser = useInternalUser();
  const { control, handleSubmit, setValue, formState, reset } = useForm<any>();

  const onCancel = () => {
    setIsOpen(false);
    reset();
  };

  const onSubmit = handleSubmit(async (data) => {
    const requestNotificationTypes = Object.keys(data).map((id) => {
      const dataItem = data[id] as { email: boolean; push: boolean };
      return {
        ...dataItem,
        notificationTypeId: Number(id)
      } as RequestNotificationType;
    });
    await dispatch(saveNotificationType(requestNotificationTypes));
    dispatch(getNotificationTypes());
    setIsOpen(false);
  });

  React.useEffect(() => {
    dispatch(getNotificationTypes());
  }, [dispatch]);

  React.useEffect(() => {
    if (notificationTypes && !formState.isDirty) {
      notificationTypes.forEach((type) => {
        setValue(`${type.notificationTypeId}.email`, type.email);
        setValue(`${type.notificationTypeId}.push`, type.push);
      });
    }
  }, [notificationTypes, setValue, formState]);

  const showNotificationsForInternalUser = (notificationTypeId: number) => {
    if (isInternalUser) {
      return [1, 2, 3, 4].includes(notificationTypeId);
    }
    return true;
  };

  return (
    <ConfirmModal
      titleText={t('notification.manageNotifications')}
      isOpen={isOpen}
      onCancel={onCancel}
      onConfirm={onSubmit}
      confirmText={t('common.save')}
    >
      <div className="manage-notifications">
        <Form onSubmit={onSubmit}>
          {notificationTypes
            ?.filter((notification) => showNotificationsForInternalUser(notification.notificationTypeId))
            .map((notificationType) => (
              <section key={notificationType.notificationTypeId} className="manage-notifications__section">
                <FormLabel>{t(`notification.manageNotifications.label.${notificationType.notificationTypeId}`)}</FormLabel>
                <div className="manage-notifications__section-item">
                  <Controller
                    name={`${notificationType.notificationTypeId}.email`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <CheckBox
                        id={`${notificationType.notificationTypeId}.email`}
                        name={`${notificationType.notificationTypeId}.email`}
                        checked={value ?? true}
                        onChange={onChange}
                        label={t('notification.emails')}
                      />
                    )}
                  />
                </div>
                <div className="manage-notifications__section-item">
                  <Controller
                    name={`${notificationType.notificationTypeId}.push`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <CheckBox
                        id={`${notificationType.notificationTypeId}.push`}
                        name={`${notificationType.notificationTypeId}.push`}
                        checked={value ?? true}
                        onChange={onChange}
                        label={t('notification.inApp')}
                      />
                    )}
                  />
                </div>
              </section>
            ))}
        </Form>
      </div>
    </ConfirmModal>
  );
};

export default ManageNotifications;
